import {
  type BoardResponse,
  boardResponseSchema,
  type CommentCreated,
  commentCreatedSchema,
  type DirectoryResponse,
  directoryResponseSchema,
  type MyTicketsResponse,
  moveBoardTicket,
  myTicketsResponseSchema,
  type QueueResponse,
  queueResponseSchema,
  type TicketCreateBody,
  type TicketDetail,
  type TicketDto,
  type TicketPatch,
  type TicketPatchResponse,
  ticketCreatedSchema,
  ticketDetailSchema,
  ticketPatchResponseSchema,
  ticketUpdatedSchema,
} from '@hrms/shared'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, apiGet, apiSend } from '../../../lib/api.ts'
import { BOARD_MOVE_MUTATION } from '../../../lib/live.ts'

export const TICKETS_KEY = ['tickets'] as const
export const MY_TICKETS_KEY = [...TICKETS_KEY, 'mine'] as const
export const ticketKeyOf = (key: string) => [...TICKETS_KEY, 'detail', key.toUpperCase()] as const
export const QUEUE_KEY = [...TICKETS_KEY, 'queue'] as const
/** PRD NFR: overdue states refresh at least every minute (the cards' own clock is `useNow`). */
const OVERDUE_REFRESH_MS = 60_000
export const BOARD_KEY = [...TICKETS_KEY, 'board'] as const
export const DIRECTORY_KEY = ['directory'] as const

/** HR queue page for a query string (the URL's, minus `ticket`). Keeps the last page while loading. */
export function useQueue(search: string) {
  return useQuery<QueueResponse>({
    queryKey: [...QUEUE_KEY, search],
    queryFn: () =>
      apiGet(`/tickets${search ? `?${search}` : ''}`, (d) => queueResponseSchema.parse(d)),
    placeholderData: keepPreviousData,
    // Server-side counts (overdue tile, overdue filter) move with the clock, not only on writes.
    refetchInterval: OVERDUE_REFRESH_MS,
  })
}

/** HR board for a filter query string (no status/sort/page). Keeps the last board while loading. */
export function useBoard(search: string) {
  return useQuery<BoardResponse>({
    queryKey: [...BOARD_KEY, search],
    queryFn: () =>
      apiGet(`/tickets/board${search ? `?${search}` : ''}`, (d) => boardResponseSchema.parse(d)),
    placeholderData: keepPreviousData,
    refetchInterval: OVERDUE_REFRESH_MS,
  })
}

export interface BoardMove {
  key: string
  id: number
  version: number
  statusId: string
  waitingMessage?: string
}

/**
 * Board drag → `PATCH {statusId, version}`. The card moves at once (optimistic); an error or a
 * 409 puts it back (the caller toasts) and everything refetches.
 */
export function useBoardMove(search: string) {
  const qc = useQueryClient()
  const boardKey = [...BOARD_KEY, search]
  return useMutation<
    TicketPatchResponse,
    Error,
    BoardMove,
    { previous: BoardResponse | undefined }
  >({
    mutationKey: BOARD_MOVE_MUTATION,
    mutationFn: ({ key, version, statusId, waitingMessage }) =>
      apiSend(
        'PATCH',
        `/tickets/${encodeURIComponent(key)}`,
        { version, statusId, ...(waitingMessage ? { waitingMessage } : {}) },
        (d) => ticketPatchResponseSchema.parse(d),
      ),
    onMutate: async (move) => {
      await qc.cancelQueries({ queryKey: boardKey })
      const previous = qc.getQueryData<BoardResponse>(boardKey)
      if (previous)
        qc.setQueryData<BoardResponse>(boardKey, {
          ...previous,
          columns: moveBoardTicket(previous.columns, move.id, move.statusId),
        })
      return { previous }
    },
    onError: (_err, _move, ctx) => {
      if (ctx?.previous) qc.setQueryData(boardKey, ctx.previous)
    },
    onSuccess: (res, move) => {
      if (!res.lostAccess) {
        const { lostAccess: _, ...detail } = res
        qc.setQueryData(ticketKeyOf(move.key), detail)
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  })
}

/** Active people (filters, assignee picker, "Raised for") and the org time zone. */
export function useDirectory(enabled = true) {
  return useQuery<DirectoryResponse>({
    queryKey: DIRECTORY_KEY,
    queryFn: () => apiGet('/tickets/directory', (d) => directoryResponseSchema.parse(d)),
    staleTime: 5 * 60_000,
    enabled,
  })
}

export function useMyTickets() {
  return useQuery<MyTicketsResponse>({
    queryKey: MY_TICKETS_KEY,
    queryFn: () => apiGet('/tickets/mine', (d) => myTicketsResponseSchema.parse(d)),
  })
}

/** One ticket with comments, activity and the people they mention. 404 → `ApiError` (no retry). */
export function useTicket(key: string | null) {
  return useQuery<TicketDetail>({
    queryKey: ticketKeyOf(key ?? ''),
    queryFn: () =>
      apiGet(`/tickets/${encodeURIComponent(key ?? '')}`, (d) => ticketDetailSchema.parse(d)),
    enabled: !!key,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
  })
}

/** After any ticket write: refresh every ticket list and this ticket's detail. */
function useInvalidate() {
  const qc = useQueryClient()
  return (ticket?: TicketDto) => {
    if (ticket) qc.invalidateQueries({ queryKey: ticketKeyOf(ticket.key) })
    return qc.invalidateQueries({ queryKey: TICKETS_KEY })
  }
}

export function useRaiseTicket() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (body: TicketCreateBody) =>
      apiSend('POST', '/tickets', body, (d) => ticketCreatedSchema.parse(d).ticket),
    onSuccess: (ticket) => invalidate(ticket),
  })
}

export function useAddComment(key: string) {
  const invalidate = useInvalidate()
  return useMutation<CommentCreated, Error, { body: string; internal?: boolean }>({
    mutationFn: (body) =>
      apiSend('POST', `/tickets/${encodeURIComponent(key)}/comments`, body, (d) =>
        commentCreatedSchema.parse(d),
      ),
    onSuccess: (res) => invalidate(res.ticket),
  })
}

export function useReopenTicket(key: string) {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (version: number) =>
      apiSend(
        'POST',
        `/tickets/${encodeURIComponent(key)}/reopen`,
        { version },
        (d) => ticketUpdatedSchema.parse(d).ticket,
      ),
    onSuccess: (ticket) => invalidate(ticket),
  })
}

/**
 * HR edits (`PATCH /api/tickets/:key`). On success the detail cache takes the response (unless
 * the change hid the ticket from the caller) and every list refreshes.
 */
export function usePatchTicket(key: string) {
  const qc = useQueryClient()
  return useMutation<TicketPatchResponse, Error, TicketPatch>({
    mutationFn: (body) =>
      apiSend('PATCH', `/tickets/${encodeURIComponent(key)}`, body, (d) =>
        ticketPatchResponseSchema.parse(d),
      ),
    onSuccess: (res) => {
      if (!res.lostAccess) {
        const { lostAccess: _, ...detail } = res
        qc.setQueryData(ticketKeyOf(key), detail)
      }
      return Promise.all([
        qc.invalidateQueries({ queryKey: QUEUE_KEY }),
        qc.invalidateQueries({ queryKey: MY_TICKETS_KEY }),
      ])
    },
  })
}

/** Per-field errors from a 400 `invalid_ticket`, if that's what `err` is. */
export function fieldErrorsOf(err: unknown): Record<string, string> | null {
  if (!(err instanceof ApiError) || err.code !== 'invalid_ticket') return null
  const fields = (err.data as { fields?: unknown } | undefined)?.fields
  return fields && typeof fields === 'object' ? (fields as Record<string, string>) : null
}
